import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Panel } from "@/components/terminal";

const STATE_CLS = {
  IDEA: "text-slate-400 border-slate-700",
  BACKTEST: "text-slate-300 border-slate-600",
  WALK_FORWARD: "text-slate-200 border-slate-500",
  PAPER: "text-sky-300 border-sky-800",
  LIVE_CONFIRM: "text-[#D4AF37] border-[#D4AF37]/50",
  LIVE_AUTO: "text-emerald-400 border-emerald-800",
  PAUSED: "text-amber-300 border-amber-800",
  RETIRED: "text-red-400 border-red-900",
};

const REGIME_CLS = {
  ELIGIBLE: "text-emerald-400 border-emerald-800",
  RESTRICTED: "text-amber-300 border-amber-800",
  PROTECTED: "text-red-400 border-red-900",
};

const slug = (k) => k.toLowerCase().replace(/_/g, "-");
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function Strategies() {
  const [rows, setRows] = useState(null);
  const [regime, setRegime] = useState(null);
  useEffect(() => {
    api.get("/strategies").then((r) => { setRows(r.data.strategies); setRegime(r.data.regime); })
      .catch(() => setRows([]));
  }, []);

  if (!rows) return <div className="text-slate-500 font-mono text-sm">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="strategies-page">
      <div>
        <h2 className="font-display text-xl font-bold" data-testid="strategies-title">Strategy Registry</h2>
        <p className="text-xs text-slate-500 font-mono mt-1 max-w-3xl">
          Authoritative lifecycle for capital-eligible strategies: IDEA → BACKTEST → WALK-FORWARD → PAPER →
          LIVE-CONFIRM → LIVE-AUTO. No strategy receives capital outside its lifecycle tier; PAUSED and RETIRED
          strategies receive none. All transitions are append-only and audit-chained.
        </p>
      </div>
      {regime && (
        <div className="panel px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="current-regime-strip">
          <span className="text-[10px] font-mono text-slate-500 tracking-widest">CURRENT REGIME</span>
          <span className="text-xs font-mono text-slate-200" data-testid="current-regime-state">{regime.state.replace(/_/g, " ")}</span>
          <span className="text-[10px] font-mono text-slate-500" data-testid="current-regime-confidence">{regime.confidence}% CONFIDENCE</span>
        </div>
      )}
      {rows.length === 0 && (
        <Panel title="Registry" testId="strategies-empty">
          <p className="text-xs text-slate-500 font-mono">No strategies registered for this account.</p>
        </Panel>
      )}
      <div className="space-y-3">
        {rows.map((s) => (
          <Panel key={s.id} title={s.name} testId={`strategy-row-${slug(s.strategy_key)}`}
            right={<span className={`text-[10px] font-mono px-2 py-0.5 border rounded-sm tracking-wide ${STATE_CLS[s.state] || "text-slate-400 border-slate-700"}`}
              data-testid={`strategy-state-badge-${slug(s.strategy_key)}`}>{s.state.replace(/_/g, "-")}</span>}>
            <p className="text-xs text-slate-500">{s.description}</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 mt-3 text-xs font-mono">
              <div>
                <div className="metric-label">Capital eligibility</div>
                <div className="text-slate-200 mt-0.5" data-testid={`strategy-eligibility-${slug(s.strategy_key)}`}>{s.eligibility}</div>
              </div>
              <div>
                <div className="metric-label">Version</div>
                <div className="text-slate-200 mt-0.5" data-testid={`strategy-version-${slug(s.strategy_key)}`}>v{s.version}</div>
              </div>
              <div>
                <div className="metric-label">Registered</div>
                <div className="text-slate-200 mt-0.5" data-testid={`strategy-created-${slug(s.strategy_key)}`}>{fmt(s.created_at)}</div>
              </div>
              <div>
                <div className="metric-label">Last transition</div>
                <div className="text-slate-200 mt-0.5" data-testid={`strategy-last-transition-${slug(s.strategy_key)}`}>
                  {s.last_transition_at ? `${fmt(s.last_transition_at)} · ${s.last_actor || "system"}` : "—"}
                </div>
              </div>
              <div>
                <div className="metric-label">Rolling score</div>
                <div className="text-slate-600 mt-0.5" data-testid={`strategy-score-${slug(s.strategy_key)}`}>— (Hussar Lab, P2)</div>
              </div>
              <div>
                <div className="metric-label">Regime eligibility</div>
                <div className="mt-0.5" data-testid={`strategy-regime-${slug(s.strategy_key)}`}>
                  <span className={`px-1.5 py-0.5 border rounded-sm text-[10px] tracking-wide ${REGIME_CLS[s.regime_eligibility] || "text-slate-400 border-slate-700"}`}
                    data-testid={`strategy-regime-badge-${slug(s.strategy_key)}`}>{s.regime_eligibility || "—"}</span>
                </div>
              </div>
            </div>
          </Panel>
        ))}
      </div>
    </div>
  );
}
